import { sortMedia } from '@pages/Library/utils/mediaSort';
import { getOmniFields, matchesOmniFilter, parseOmniQuery } from '@pages/Library/utils/omniFilter';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { useMemo } from 'react';
import { MediaTableRow } from '../cells/types';

export const useMediaData = () => {
  const folder = useLibraryFolder();
  const uploadManager = useUploadManager();

  // Determine if we're still loading initial data from the API
  const isLoading = folder.fetching;

  // Use filesForTable (no title pre-filter) so DataTable can apply filterModel (including multiple Media name criteria) client-side.
  // Filter uploads and hide empty status media; attach upload state and virtual rows for smooth transition.
  const mediaData = useMemo(() => {
    // Include 'success' so we keep showing the row until folder has it (avoids disappear-reappear jank)
    const activeUploadStatuses = Object.values(uploadManager.uploadStatus).filter(
      (s) =>
        (s.status === 'queued' || s.status === 'uploading' || s.status === 'success') &&
        folder.holdsUpload(s.destination),
    );

    // Get existing media from filesForTable (no title filter), attaching upload status where applicable.
    // Always hide empty media; the uploading card is shown via virtual upload row.
    const existingMedia = folder.filesForTable
      .filter((file) => file.status !== 'empty')
      .map((file) => {
        const uploadUuid = uploadManager.mediaIdToLocalUUID[file._id];
        const uploadStatus = uploadUuid ? uploadManager.uploadStatus[uploadUuid] : undefined;
        const isCurrentlyUploading =
          uploadStatus && (uploadStatus.status === 'queued' || uploadStatus.status === 'uploading');

        if (isCurrentlyUploading) {
          return {
            ...file,
            isUploading: true,
            uploadStatus,
          } as MediaTableRow;
        }
        return file as MediaTableRow;
      });

    // Only dedupe virtual upload when backend media has moved past "empty" (avoids empty blip while upload finishes)
    const existingNonEmptyMediaIds = new Set(
      existingMedia.filter((f) => f.status !== 'empty').map((f) => f._id),
    );

    // Create virtual rows for uploads that don't have backend media yet (or backend media still "empty")
    const uploadingFiles = activeUploadStatuses
      .filter((status) => {
        if (status.media?._id && existingNonEmptyMediaIds.has(status.media._id)) {
          return false;
        }
        return true;
      })
      .map((status) => {
        if (status.status === 'success' && status.media) {
          return {
            ...status.media,
            metadata: {
              ...status.media.metadata,
              // Preserve local filename as fallback until backend populates metadata.name
              name: status.media.metadata?.name ?? status.metadata?.name,
              video: {
                ...status.media.metadata?.video,
                // Preserve local duration as fallback until backend populates it after processing.
                // Use || (not ??) so backend's 0 (unprocessed) falls through to local duration.
                duration: status.media.metadata?.video?.duration || status.metadata?.duration || 0,
              },
            },
            isUploading: false,
            uploadStatus: status,
            status: 'completed' as const,
          } as unknown as MediaTableRow;
        }
        return {
          _id: status.media?._id ?? status.uuid,
          filename: status.file.name,
          status: status.status,
          progress: status.progress / 100, // Convert to 0-1 range
          createdAt: status.createdAt, // Use stable timestamp from when queued
          metadata: {
            name: status.file.name,
            filename: status.file.name,
            video: {
              // Use locally-extracted duration if available
              duration: status.metadata?.duration ?? 0,
              width: 0,
              height: 0,
              // Backend uses originalMediaSize in MB; file.size is bytes → convert to MB
              originalMediaSize: status.file.size / (1024 * 1024),
            },
          },
          size: status.file.size,
          libraryId: status.destination.libraryId,
          folderPath: status.destination.folderPath,
          uniqueId: '',
          thumbnailUrl: '',
          videoUrl: '',
          statusMessage: '',
          errorMessage: '',
          isUploading: true,
          uploadStatus: status,
          userMetadata: status.userMetadata,
        } as unknown as MediaTableRow;
      });

    // Merge, apply the omnifilter, then sort. Filtering the rows here (rather than via
    // the DataTable filterModel) keeps the grid's filtered-row stats correct while
    // letting per-column filters stack on top. Same predicate as the grid view.
    const parsed = parseOmniQuery(folder._omniFilter, omniFilterPreferences.matchAll);
    const combined = parsed
      ? [...uploadingFiles, ...existingMedia].filter((row) =>
          matchesOmniFilter(getOmniFields(row), parsed),
        )
      : [...uploadingFiles, ...existingMedia];

    return sortMedia(combined, folder.effectiveSortModel);
  }, [
    folder.filesForTable,
    folder._omniFilter,
    omniFilterPreferences.matchAll,
    folder.effectiveSortModel,
    folder.library?.libraryId,
    folder.currentFolderPath,
    uploadManager.uploadStatus,
    uploadManager.mediaIdToLocalUUID,
  ]);

  return { data: mediaData, isLoading };
};
